import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ChartLineUp, ListMagnifyingGlass, Table, SignOut, List, X, Sparkle, BellRinging } from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: ChartLineUp, id: "dashboard" },
  { to: "/queries", label: "Tracked Queries", icon: ListMagnifyingGlass, id: "queries" },
  { to: "/citations", label: "Citations", icon: Table, id: "citations" },
  { to: "/settings", label: "Alerts", icon: BellRinging, id: "settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-6 py-6 border-b border-border">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-action">
          <Sparkle size={18} weight="fill" className="text-white" />
        </div>
        <span className="font-heading text-lg font-extrabold tracking-tighter">Citelytics</span>
      </div>
      <nav className="flex-1 px-3 py-6 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`nav-${n.id}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 border-l-2 ${
                isActive
                  ? "border-action bg-secondary text-foreground font-semibold"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/50"
              }`
            }
          >
            <n.icon size={18} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-4">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="h-8 w-8">
            {user?.picture && <AvatarImage src={user.picture} alt={user?.name} />}
            <AvatarFallback className="bg-secondary text-xs">
              {(user?.name || "U").slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium" data-testid="user-name">{user?.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={doLogout}
          data-testid="logout-button"
          className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
        >
          <SignOut size={16} /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="grain min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-60 border-r border-border bg-background md:block">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r border-border bg-background">
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className="md:pl-60">
        {/* Mobile header */}
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/70 px-4 py-3 backdrop-blur-xl md:hidden">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-action">
              <Sparkle size={16} weight="fill" className="text-white" />
            </div>
            <span className="font-heading font-extrabold tracking-tighter">Citelytics</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={20} /> : <List size={20} />}
          </Button>
        </header>
        <main className="relative z-[1]">{children}</main>
      </div>
    </div>
  );
}
